import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormLabel, FormError, FormHelpText } from 'components/FormTexts';
import InputMasked from 'react-maskedinput';
import * as Styled from './Input.styled';

//Input typically used in a form component.
//This can be both a controlled input or an uncontrolled input. If a ref is passed and no value prop is passed then it becomes a uncontrolled component. useful for hook-forms.
// Apollo Input https://www.figma.com/file/snfUy91C2T3s4KpS1iovGz/Apollo-Web0.0.1?node-id=3251%3A73957
const Input = (props) => {
  const {
    className,
    classes,
    startAdornment,
    endAdornment,
    name,
    mask,
    placeholder,
    label,
    helpText,
    error,
    disabled,
    verified,
    maxlength,
    inputRef,
    showStartAdornmentDivider,
    showEndAdornmentDivider,
    showStartAdornmentBackground,
    showEndAdornmentBackground,
    onFocus,
    onBlur,
    showInlineIcons: showInlineIconsProp,
    labelId,
    ...passThroughProps
  } = props;

  // Used when adornment is present.
  const [focused, setFocused] = useState(false);

  const handleFocus = useCallback(
    (event) => {
      if (onFocus) {
        onFocus(event);
      }
      setFocused(true);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (event) => {
      if (onBlur) {
        onBlur(event);
      }
      setFocused(false);
    },
    [onBlur]
  );

  // Determine whether to show red/green/grey border and inline icons.
  const inputState = useMemo(() => {
    if (error) return 'error';
    return verified ? 'verified' : '';
  }, [error, verified]);

  //Display the icons at the end of the input only if there is an error or the input is verified.
  const showInlineIcons = !!(showInlineIconsProp && !!inputState && !disabled);

  const StyledInputComponent = Styled.BaseInput;

  return (
    <Styled.RootComponent className={classnames(classes.root, className)}>
      {label && (
        <FormLabel
          id={labelId}
          className={classes.label}
          hasHelpText={helpText ? true : false}
        >
          {label}
        </FormLabel>
      )}
      {helpText && (
        <FormHelpText className={classes.helpText}>{helpText}</FormHelpText>
      )}
      <Styled.InputWrapper
        className={classes.inputAdornmentContainer}
        state={inputState}
        disabled={disabled}
        focused={focused ? 'true' : ''}
      >
        {startAdornment && (
          <Styled.LeftAdornmentContainer
            className={classnames(classes.startAdornmentContainer, {
              [classes.startAdornmentError]: !!error,
            })}
            divider={showStartAdornmentDivider ? 'true' : ''}
            background={showStartAdornmentBackground ? 'true' : ''}
            state={inputState}
            disabled={disabled}
          >
            {startAdornment}
          </Styled.LeftAdornmentContainer>
        )}
        <Styled.InputContainer>
          <StyledInputComponent
            state={inputState}
            className={classes.input}
            showInlineIcons={showInlineIcons}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            maxlength={maxlength}
            ref={inputRef}
            mask={mask}
            onBlur={handleBlur}
            onFocus={handleFocus}
            component={mask && InputMasked}
            aria-labelledby={labelId}
            {...passThroughProps}
          />
          {showInlineIcons && (
            <>
              {inputState === 'error' ? (
                <Styled.ErrorIcon className={classes.inlineIcon} />
              ) : (
                <Styled.VerifiedIcon className={classes.inlineIcon} />
              )}
            </>
          )}
        </Styled.InputContainer>
        {endAdornment && (
          <Styled.RightAdornmentContainer
            className={classnames(classes.endAdornmentContainer, {
              [classes.endAdornmentError]: !!error,
            })}
            disabled={disabled}
            state={inputState}
            divider={showEndAdornmentDivider ? 'true' : ''}
            background={showEndAdornmentBackground ? 'true' : ''}
          >
            {endAdornment}
          </Styled.RightAdornmentContainer>
        )}
      </Styled.InputWrapper>
      {error && typeof error === 'string' && <FormError>{error}</FormError>}
    </Styled.RootComponent>
  );
};

Input.propTypes = {
  className: PropTypes.string,
  startAdornment: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  endAdornment: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  showStartAdornmentDivider: PropTypes.bool,
  showEndAdornmentDivider: PropTypes.bool,
  showStartAdornmentBackground: PropTypes.bool,
  showEndAdornmentBackground: PropTypes.bool,
  mask: PropTypes.string, //when you want input masking, use "1" as the token e.g. 11/11/1111
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // if boolean then no error in shown below.
  classes: PropTypes.shape({
    root: PropTypes.string,
    input: PropTypes.string,
    label: PropTypes.string,
    helpText: PropTypes.string,
    error: PropTypes.string,
    startAdornmentContainer: PropTypes.string,
    endAdornmentContainer: PropTypes.string,
    inlineIcon: PropTypes.string,
    startAdornmentError: PropTypes.string,
    endAdornmentError: PropTypes.string,
    inputAdornmentContainer: PropTypes.string, //styles to element containing both input and adornment.
  }),
  disabled: PropTypes.bool,
  verified: PropTypes.bool,
  maxlength: PropTypes.number,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  showInlineIcons: PropTypes.bool, // Display the error and verified icons with in the input.
  labelId: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

Input.defaultProps = {
  className: '',
  startAdornment: null,
  endAdornment: null,
  mask: null,
  placeholder: '',
  label: '',
  helptext: '',
  disabled: false,
  error: false,
  verified: false,
  maxlength: 524288, //The maximum number of characters allowed in the <input> element. Default value is 524288
  inputRef: null,
  classes: {
    root: '',
    input: '',
    label: '',
    helpText: '',
    error: '',
    adornmentContainer: '',
    inlineIcon: '',
    adornmentError: '',
    inputAdornmentContainer: '',
  },
  showInlineIcons: true,
  showStartAdornmentDivider: false,
  showEndAdornmentDivider: true,
  showStartAdornmentBackground: true,
  showEndAdornmentBackground: true,
  labelId: '',
};

export default Input;
